import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Award, Users, ShieldCheck, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker } from "@/components/form";
import { Reviews } from "@/components/Reviews";
import { Newsletter } from "@/components/Newsletter";
import { AREAS, CONTACT } from "@/data/site";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9cdaf805-ebb2-4698-b869-6dae56934adb/images/113458cf5c4b3a0600a26a2483cd5e6594d6043ec2f001d73eb14cbffcf8f1af.png";
const ADVISORY_IMG = "https://static.prod-images.emergentagent.com/jobs/9cdaf805-ebb2-4698-b869-6dae56934adb/images/f063d554fd7fe6986ad388e8d6c9ff4637eb605a3cd4512380953b109107b93f.png";

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo titleOverride="VMF Versicherungsmakler Frankfurt | Versicherungen, Immobilien & Finanzierung" ogTitle="VMF Versicherungsmakler Frankfurt – Sorglos und sicher in Ihre Zukunft." description="Unabhängige Beratung für Versicherungen, Immobilien und Finanzierung in Frankfurt am Main. Persönlich, digital und zuverlässig." path="/" keywords="Versicherungsmakler Frankfurt, unabhängiger Makler, Baufinanzierung, Immobilien Frankfurt, Finanzberatung Rhein-Main" />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Frankfurt Skyline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-900/80" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-20 w-full">
          <motion.div {...fade} className="max-w-3xl">
            <Kicker>Unabhängiger Makler · Frankfurt am Main</Kicker>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-white text-shadow-navy">
              Ihre Werte.<br /><span className="text-gold-500">Optimal geschützt.</span>
            </h1>
            <p className="mt-6 flex items-center gap-3 text-base sm:text-lg font-medium tracking-wide text-gold-400">
              <span className="h-px w-8 bg-gold-500/60" aria-hidden="true" />
              Sorglos und sicher in Ihre Zukunft.
            </p>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-xl">
              Versicherung, Finanzierung und Immobilien aus einer Hand. Unabhängig, transparent und persönlich – für Privatkunden, Selbstständige und Unternehmen.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/termin-buchen" data-testid="hero-termin-btn" className="group bg-gold-500 text-navy-900 font-bold tracking-wide rounded-sm px-8 py-4 flex items-center gap-2 hover:bg-gold-300 transition-colors">
                Kostenlose Beratung <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/kontakt" data-testid="hero-kontakt-btn" className="border border-white/20 text-white font-medium rounded-sm px-8 py-4 hover:border-gold-500 hover:text-gold-500 transition-colors">
                Kontakt aufnehmen
              </Link>
            </div>
            <div className="mt-14 flex flex-wrap gap-8 text-sm text-slate-300">
              {["400+ Bankpartner", "Über 20 Jahre Erfahrung", "Unabhängige Beratung"].map((t) => (
                <span key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-gold-500" /> {t}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* AREAS */}
      <section className="bg-navy-900 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fade} className="max-w-2xl">
            <Kicker>Unsere Geschäftsbereiche</Kicker>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-white">Drei Kompetenzen. Ein Partner.</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">Ganzheitliche Beratung – abgestimmt auf Ihre persönliche und wirtschaftliche Situation.</p>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {AREAS.map((a, i) => (
              <motion.div key={a.key} {...fade} transition={{ duration: 0.6, delay: i * 0.1 }}>
                <Link to={`/${a.slug}`} data-testid={`area-card-${a.key}`}
                  className="group block h-full bg-navy-800 border border-white/5 rounded-md p-8 lg:p-10 hover:-translate-y-1 hover:border-gold-500/50 hover:shadow-[0_10px_40px_rgba(212,175,55,0.1)] transition-[transform,border-color,box-shadow] duration-300">
                  <a.icon size={40} className="text-gold-500" strokeWidth={1.4} />
                  <h3 className="mt-6 text-2xl font-bold text-white tracking-tight">{a.title}</h3>
                  <p className="mt-3 text-slate-400 leading-relaxed">{a.tagline}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-gold-500 text-sm font-medium">Mehr erfahren <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / WHY US */}
      <section className="bg-offwhite py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fade} className="relative">
            <img src={ADVISORY_IMG} alt="Beratung" className="rounded-md w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-6 -right-6 hidden sm:block bg-navy-900 text-white rounded-md p-6 shadow-xl">
              <p className="font-serif text-4xl text-gold-500">98%</p>
              <p className="text-xs text-slate-300 mt-1 max-w-[140px]">Weiterempfehlungs&shy;quote unserer Mandanten</p>
            </div>
          </motion.div>
          <motion.div {...fade}>
            <Kicker>Warum VM-FFM.DE</Kicker>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-navy-900">Beratung, die sich an Ihnen orientiert.</h2>
            <div className="mt-10 space-y-8">
              {[
                { icon: ShieldCheck, t: "Wirklich unabhängig", d: "Wir sind ausschließlich Ihnen verpflichtet – nicht einer einzelnen Gesellschaft." },
                { icon: Award, t: "Ausgezeichnete Expertise", d: "Zertifizierte Berater mit langjähriger Markterfahrung." },
                { icon: Users, t: "Persönlich & langfristig", d: "Ein fester Ansprechpartner, der Sie über Jahre begleitet." },
                { icon: TrendingUp, t: "Beste Konditionen", d: "Durch starke Marktanbindung sichern wir Top-Preise für Sie." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <div className="w-11 h-11 rounded-sm bg-navy-900 flex items-center justify-center shrink-0"><f.icon size={20} className="text-gold-500" /></div>
                  <div>
                    <h4 className="font-bold text-navy-900 text-lg">{f.t}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed mt-1">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Reviews />
      <Newsletter />

      {/* CTA */}
      <section className="bg-navy-800 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 text-left lg:text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">Lassen Sie uns sprechen.</h2>
          <p className="mt-4 text-slate-300 leading-relaxed lg:max-w-2xl lg:mx-auto">Vereinbaren Sie ein unverbindliches Erstgespräch. Wir nehmen uns Zeit für Ihre Anliegen – kostenlos und ohne Verpflichtung.</p>
          <div className="mt-10 flex flex-wrap gap-4 lg:justify-center">
            <Link to="/termin-buchen" className="bg-gold-500 text-navy-900 font-bold rounded-sm px-8 py-4 hover:bg-gold-300 transition-colors">Termin vereinbaren</Link>
            <a href={`tel:${CONTACT.phoneHref}`} className="border border-white/20 text-white rounded-sm px-8 py-4 hover:border-gold-500 hover:text-gold-500 transition-colors">{CONTACT.phone}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
